import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTab } from '../../contexts/TabContext';
import { useZoeControls } from '../../contexts/ZoeConversationContext';
import ZoeMark from './ZoeMark';
import ZoePopup from './ZoePopup';

/** The full ZOE page's tab id — the launcher hides there, where it would be redundant. */
const ZOE_PAGE_TAB = 'agents';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
const ZOE_SHORTCUT_LABEL = IS_MAC ? '⌘J' : 'Ctrl+J';

interface ZoeLauncherProps {
  /** Label of the active page, shown in the pop-up header. */
  pageLabel?: string;
}

/**
 * Floating ZOE button, bottom-right on every page except the ZOE page, plus
 * its pop-up. ⌘J / Ctrl+J toggles it. Render only for users who can open ZOE.
 * Reads only ZOE's controls, so it doesn't re-render while an answer streams.
 */
export default function ZoeLauncher({ pageLabel }: ZoeLauncherProps) {
  const { activeTab, setActiveTab } = useTab();
  const { isLoading, popupOpen, setPopupOpen } = useZoeControls();
  const [hasOpened, setHasOpened] = useState(false);
  const [unseenAnswer, setUnseenAnswer] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const onZoePage = activeTab === ZOE_PAGE_TAB;
  const open = popupOpen && !onZoePage;

  useEffect(() => {
    if (open) {
      setHasOpened(true);
      setUnseenAnswer(false);
    }
  }, [open]);

  // Navigating to the ZOE page closes the pop-up rather than leaving it
  // open-but-hidden to reappear on the next page. The page shows the thread.
  useEffect(() => {
    if (onZoePage && popupOpen) setPopupOpen(false);
    if (onZoePage) setUnseenAnswer(false);
  }, [onZoePage, popupOpen, setPopupOpen]);

  // An answer (or error) that arrives while nobody is looking marks the button.
  const wasLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && !open && !onZoePage) setUnseenAnswer(true);
    wasLoadingRef.current = isLoading;
  }, [isLoading, open, onZoePage]);

  // ⌘J / Ctrl+J toggles the pop-up from anywhere in the app.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'j') return;
      if (onZoePage) return;
      e.preventDefault();
      setPopupOpen(!popupOpen);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onZoePage, popupOpen, setPopupOpen]);

  // However the pop-up closes (Esc, ✕, ⌘J, "Open full ZOE", navigating to
  // the ZOE page), focus inside it goes back to the launcher — or, where the
  // launcher isn't shown, off the now-inert panel.
  const returnFocus = useCallback(() => {
    if (buttonRef.current) buttonRef.current.focus();
    else (document.activeElement as HTMLElement | null)?.blur();
  }, []);

  const close = () => setPopupOpen(false);

  const openFullPage = () => {
    setPopupOpen(false);
    setActiveTab(ZOE_PAGE_TAB);
  };

  if (onZoePage && !hasOpened) return null;

  const dot = !open && (isLoading ? 'busy' : unseenAnswer ? 'ready' : null);
  const label = open ? 'Close ZOE' : `Ask ZOE (${ZOE_SHORTCUT_LABEL})`;

  return (
    <>
      {hasOpened ? (
        <ZoePopup
          open={open}
          pageLabel={pageLabel}
          onClose={close}
          onOpenFullPage={openFullPage}
          onReturnFocus={returnFocus}
        />
      ) : null}

      {!onZoePage ? (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setPopupOpen(!popupOpen)}
          aria-label={label}
          title={label}
          aria-expanded={open}
          aria-controls={hasOpened ? 'zoe-popup' : undefined}
          className={`fixed z-40 bottom-6 right-6 w-14 h-14 rounded-full grid place-items-center border transition-transform-box-shadow-background duration-150 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-zoe-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
            open
              ? 'bg-secondary border-accent text-foreground shadow-lg shadow-black/40'
              : 'bg-gradient-to-br from-zoe-500 to-zoe-700 border-zoe-300/40 text-white shadow-lg shadow-zoe-700/50 hover:scale-105 hover:shadow-zoe-500/40'
          }`}
        >
          {open ? <X className="w-6 h-6" /> : <ZoeMark className="w-7 h-7" />}
          {dot ? (
            <span
              aria-hidden
              className={`absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${
                dot === 'busy' ? 'bg-zoe-300 animate-pulse' : 'bg-success'
              }`}
            />
          ) : null}
          {dot ? (
            <span className="sr-only">{dot === 'busy' ? 'ZOE is working on an answer' : 'A new answer from ZOE is ready'}</span>
          ) : null}
        </button>
      ) : null}
    </>
  );
}
